import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { Loader2, Check, Image as ImageIcon, AlertCircle, Upload, X } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { atendimentoApi } from '../lib/api';
import type { PerfilInbox } from '../lib/tipos';

const FOTO_OUTPUT_PX = 640; // Meta recomenda >=192, max 640

function carregarImagem(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function gerarJpegBase64Quadrado(srcDataUrl: string, area: Area): Promise<string> {
  const img = await carregarImagem(srcDataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = FOTO_OUTPUT_PX;
  canvas.height = FOTO_OUTPUT_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas indisponivel');
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, FOTO_OUTPUT_PX, FOTO_OUTPUT_PX);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
  return dataUrl.split(',')[1] ?? '';
}

interface Props {
  aberto: boolean;
  onFechar: () => void;
  // embutido: renderiza so o conteudo (sem o wrapper Modal), pra encaixar numa aba.
  embutido?: boolean;
}

// Verticais oficiais Meta WhatsApp Business
const VERTICAIS = [
  { v: 'UNDEFINED', label: '— Nao definido —' },
  { v: 'RETAIL', label: 'Varejo' },
  { v: 'AUTO', label: 'Automotivo' },
  { v: 'BEAUTY', label: 'Beleza' },
  { v: 'APPAREL', label: 'Vestuario' },
  { v: 'EDU', label: 'Educacao' },
  { v: 'ENTERTAIN', label: 'Entretenimento' },
  { v: 'EVENT_PLAN', label: 'Eventos' },
  { v: 'FINANCE', label: 'Financeiro' },
  { v: 'GROCERY', label: 'Mercado' },
  { v: 'HEALTH', label: 'Saude' },
  { v: 'HOTEL', label: 'Hotelaria' },
  { v: 'NONPROFIT', label: 'Sem fins lucrativos' },
  { v: 'PROF_SERVICES', label: 'Servicos profissionais' },
  { v: 'TRAVEL', label: 'Viagem' },
  { v: 'RESTAURANT', label: 'Restaurante' },
  { v: 'GOVT', label: 'Governamental' },
  { v: 'OTHER', label: 'Outro' },
];

export function ModalPerfilInbox({ aberto, onFechar, embutido = false }: Props) {
  const toast = useToast();

  const [perfil, setPerfil] = useState<PerfilInbox | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Form state
  const [about, setAbout] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [vertical, setVertical] = useState('UNDEFINED');

  // Cropper state
  const [srcArquivo, setSrcArquivo] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaCrop, setAreaCrop] = useState<Area | null>(null);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const [fotoAtualVer, setFotoAtualVer] = useState(0); // bust cache do <img> apos upload
  const fileRef = useRef<HTMLInputElement | null>(null);

  const onCropComplete = useCallback((_: Area, area: Area) => setAreaCrop(area), []);

  function escolherArquivo() {
    fileRef.current?.click();
  }

  function aoEscolherArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.erro('Selecione uma imagem (JPG ou PNG)');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.erro('Imagem maior que 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSrcArquivo(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setAreaCrop(null);
    };
    reader.readAsDataURL(f);
    // reset pra permitir escolher o mesmo arquivo dnv depois
    e.target.value = '';
  }

  function cancelarCrop() {
    setSrcArquivo(null);
    setAreaCrop(null);
  }

  async function salvarFoto() {
    if (!srcArquivo || !areaCrop || enviandoFoto) return;
    setEnviandoFoto(true);
    try {
      const base64 = await gerarJpegBase64Quadrado(srcArquivo, areaCrop);
      await atendimentoApi.enviarFotoPerfilInbox({ base64, mime: 'image/jpeg' });
      toast.sucesso('Foto atualizada no WhatsApp Business');
      setSrcArquivo(null);
      setFotoAtualVer((v) => v + 1);
      // Recarrega perfil pra pegar nova profile_picture_url da Meta
      const p = await atendimentoApi.obterPerfilInbox('atendimento');
      setPerfil(p);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao enviar foto');
    } finally {
      setEnviandoFoto(false);
    }
  }

  useEffect(() => {
    if (!aberto) return;
    let cancelado = false;
    setCarregando(true);
    setErro(null);
    atendimentoApi.obterPerfilInbox('atendimento')
      .then((p) => {
        if (cancelado) return;
        setPerfil(p);
        setAbout(p.about ?? '');
        setAddress(p.address ?? '');
        setDescription(p.description ?? '');
        setEmail(p.email ?? '');
        setWebsite((p.websites && p.websites[0]) ?? '');
        setVertical(p.vertical ?? 'UNDEFINED');
      })
      .catch((e: any) => { if (!cancelado) setErro(e?.message ?? 'Falha ao carregar perfil'); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [aberto]);

  async function salvar() {
    if (salvando) return;
    setSalvando(true);
    try {
      const websites = website.trim() ? [website.trim()] : [];
      await atendimentoApi.atualizarPerfilInbox('atendimento', {
        about: about.trim(),
        address: address.trim(),
        description: description.trim(),
        email: email.trim(),
        websites,
        vertical,
      });
      toast.sucesso('Perfil atualizado no WhatsApp Business');
      if (!embutido) onFechar();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  const btnSalvar = (
    <button
      type="button"
      className="btn-primario flex items-center gap-1.5"
      onClick={salvar}
      disabled={salvando || carregando || !!erro}
    >
      {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
      {salvando ? 'Salvando...' : 'Salvar'}
    </button>
  );

  const corpo = (
    <>
      <div className="text-xs opacity-70 mb-3">
        Edita os dados do perfil <b>Atendimento (+55 64 99259-7498)</b> via Cloud API. Compras (Baileys) nao usa perfil business — e numero pessoal.
      </div>

      {carregando && (
        <div className="flex items-center justify-center py-8 opacity-70">
          <Loader2 size={18} className="animate-spin" />
        </div>
      )}

      {erro && (
        <div className="flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
          <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <span className="text-sm">{erro}</span>
        </div>
      )}

      {!carregando && !erro && perfil && (
        <div className="space-y-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={aoEscolherArquivo}
            className="hidden"
          />

          {srcArquivo ? (
            <div className="space-y-2 p-2 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
              <div className="flex items-center gap-1 text-xs opacity-70 font-medium">
                <ImageIcon size={11} /> Ajuste o enquadramento (quadrado)
              </div>
              <div className="relative w-full bg-black/40 rounded" style={{ height: 280 }}>
                <Cropper
                  image={srcArquivo}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] opacity-60 w-10">Zoom</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="btn-secundario flex items-center gap-1"
                  onClick={cancelarCrop}
                  disabled={enviandoFoto}
                >
                  <X size={14} /> Cancelar
                </button>
                <button
                  type="button"
                  className="btn-primario flex items-center gap-1"
                  onClick={salvarFoto}
                  disabled={enviandoFoto || !areaCrop}
                >
                  {enviandoFoto ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {enviandoFoto ? 'Enviando...' : 'Salvar foto'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-2 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
              {perfil.profile_picture_url ? (
                <img
                  src={perfil.profile_picture_url + (fotoAtualVer ? `#v=${fotoAtualVer}` : '')}
                  alt="Foto perfil"
                  className="w-14 h-14 rounded-full object-cover"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gray-300 flex items-center justify-center text-gray-500">
                  <ImageIcon size={24} />
                </div>
              )}
              <div className="flex-1">
                <div className="text-xs opacity-70 flex items-center gap-1 font-medium">
                  <ImageIcon size={11} /> Foto do perfil WA Business
                </div>
                <div className="text-[11px] opacity-50">JPG ou PNG, ate 10MB. Salvo como quadrado 640x640</div>
              </div>
              <button
                type="button"
                className="btn-secundario flex items-center gap-1 flex-shrink-0"
                onClick={escolherArquivo}
              >
                <Upload size={13} /> Trocar foto
              </button>
            </div>
          )}

          <div>
            <label className="text-xs opacity-70 block mb-0.5">Sobre (status)</label>
            <input
              type="text"
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              maxLength={139}
              placeholder="Frase curta exibida no perfil"
              className="w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
              style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
            />
            <div className="text-[10px] opacity-50 mt-0.5 text-right">{about.length}/139</div>
          </div>

          <div>
            <label className="text-xs opacity-70 block mb-0.5">Descricao</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={512}
              rows={3}
              placeholder="Descricao da empresa"
              className="w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400 resize-y"
              style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
            />
            <div className="text-[10px] opacity-50 mt-0.5 text-right">{description.length}/512</div>
          </div>

          <div>
            <label className="text-xs opacity-70 block mb-0.5">Endereco</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              maxLength={256}
              placeholder="Av. ..., Mineiros - GO"
              className="w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
              style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs opacity-70 block mb-0.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={128}
                placeholder="contato@fiodecobre.com.br"
                className="w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
                style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
              />
            </div>
            <div>
              <label className="text-xs opacity-70 block mb-0.5">Website</label>
              <input
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://fiodecobre.com.br"
                className="w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
                style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
              />
            </div>
          </div>

          <div>
            <label className="text-xs opacity-70 block mb-0.5">Categoria do negocio</label>
            <select
              value={vertical}
              onChange={(e) => setVertical(e.target.value)}
              className="w-full px-2 py-1.5 rounded border text-sm"
              style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '14px' }}
            >
              {VERTICAIS.map((v) => <option key={v.v} value={v.v}>{v.label}</option>)}
            </select>
          </div>
        </div>
      )}
    </>
  );

  if (embutido) {
    return (
      <div>
        {corpo}
        <div className="flex justify-end pt-3">{btnSalvar}</div>
      </div>
    );
  }

  return (
    <Modal aberto={aberto} onFechar={salvando ? () => {} : onFechar} titulo="Perfil WhatsApp Business" largura={560}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={onFechar} disabled={salvando}>Cancelar</button>
          {btnSalvar}
        </>
      }
    >
      {corpo}
    </Modal>
  );
}
